import React, {useState, useCallback, useEffect, useMemo} from "react";
import {Link, NavLink, Prompt, Redirect, Route, Switch, useHistory, useParams, useRouteMatch} from "react-router-dom";
import {FormattedMessage, useIntl} from "react-intl";
import { BookOpen, Files, Settings, Loader2 } from "lucide-react";
import { ReactComponent as BackArrow } from "../../svg/back.svg";
import { ReactComponent as TestIcon } from "../../svg/test.svg";
import {ReactComponent as StatusIcon} from "../../svg/status.svg";
import {Modal, PopupProvider, Skeleton, StatusText, usePopup} from "../../UIElements";
import BotVersionSelector from "./components/BotVersionSelector";
import "./SupportBotEditor.scss"
import BotInformation from "./BotInformation";
import {useSupportBotData} from "./SupportBotData";
import BotInstructions from "./BotInstructions";
import BotKnowledge from "./BotKnowledge";
import {useSnackbar} from "../../Content";
import {
    AddFileToVectorStore,
    CreateVectorStore,
    GetAssistant, GetVectorStoreFiles, PostAssistant, PostAssistantWithId, PostBot, PostBotWithId, RemoveFileFromVectorStore,
    updateBotById
} from "../../api/APIBot";
import {queryClient} from "../../App";
import {PostAIAgent, PostAIAgentWithId, GetAIAgent} from "../../api/APIGemini";
import {getUniqueKey} from "../../Formats";
import BotModuleTester from "./components/BotModuleTester";
import {useLanguageData} from "../../App";
import {processSingleBotInstructions, replaceInstructionVariables, mergeGeminiTemplates, getLanguageDisplayName} from "../../utils/assistantUtils";
import {syncVectorStoreFiles, ensureFileSearchTool} from "../../utils/vectorStoreUtils";
import {ensureRagRetrievalTool} from "../../utils/ragCorpusUtils";
import {buildGeminiAgentData} from "../../utils/geminiConfigUtils";
import {getTopicDisplayNameFromTranslations} from "../../utils/topicUtils";
import {GetGeneralTranslations} from "../../api/APISurvey";
import {formatTranslationsToIntl} from "../../Formats";
import BotSessionData from "../../utils/botSessionStorage";
import _ from "lodash";

export const SupportBotEditor = () => {
    const { botId } = useParams();
    const intl = useIntl();
    let { url, path } = useRouteMatch();
    const [initialDataLoaded, setInitialDataLoaded] = useState(false)

    const {
        isLoading,
        isError,
        isSuccess,
        refreshData,
        data: {
            supportRoles,
            supportBots,
            supportTopicCodes
        } = {}
    } = useSupportBotData();

    useEffect(() => {
        if (isSuccess && supportBots && !initialDataLoaded) {
            setInitialDataLoaded(true)
        }
    }, [isSuccess, supportBots, initialDataLoaded])

    if (isLoading) {
        return <BotEditorSkeleton />
    }

    if (isError) {
        return <div className={"survey-container bot-editor"}>
            <div className={"survey-content"}>
                <h3>
                    {intl.formatMessage({
                        defaultMessage: 'Loading error. Please try again later.',
                        id: 'error.loading'
                    })}
                </h3>
            </div>
        </div>
    }

    return isSuccess && supportBots ? (
        <BotEditorContent
            botId={botId}
            url={url}
            path={path}
            supportBots={supportBots}
            supportTopicCodes={supportTopicCodes}
            refreshData={refreshData}
            initialDataLoaded={initialDataLoaded}
        />
    ) : null;
};

const defaultBotData = {
    id: null,
    updated: "",
    updatedby: "",
    created: "",
    createdby: "",
    status: "DRAFT",
    config: {
        name: "",
        description: "",
        versionGroup: "",
        versions: [],
        version: "DEFAULT",
        coordinator: "",
        supportRequestTarget: "",
        generalInstructions: "",
        supportTopics: [],
        materials: [],
        color: "#9EA5F8",
        icon: "Bot",
        topicData: {},
        topicSettings: {},
    }
}

const BotEditorContent = ({ botId, url, path, supportBots, supportTopicCodes, refreshData, initialDataLoaded }) => {
    const intl = useIntl();
    const [botData, setBotData] = useState(null);
    const [originalBotData, setOriginalBotData] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [isSaving, setIsSaving] = useState(null); // null, 'DRAFT', or 'ACTIVE'
    const { setSnackbarData } = useSnackbar();
    const newBot = botId === 'new'
    const postBot = newBot ? PostBot() : PostBotWithId(botId);
    const { showPopup, hidePopup } = usePopup()
    let history = useHistory()

    // Add assistant-related hooks
    const {
        aiProvider,
        data: {
            assistantInstructionsConfig,
            supportBotInstructions,
            supportTopicInstructionBody,
            supportBotInstructionBody,
            geminiConfig
        } = {},
    } = useSupportBotData();

    const isGemini = aiProvider === "gemini";

    const { instanceLang } = useLanguageData();
    const botLanguage = instanceLang || "en";
    const botLangTranslations = GetGeneralTranslations(botLanguage);

    // OpenAI hooks
    const postAssistant = PostAssistant();
    const postAssistantWithId = PostAssistantWithId(botData?.assistantid);
    const getAssistant = GetAssistant(botData?.assistantid, !isGemini && !!botData?.assistantid);

    // Gemini hooks
    const postAIAgent = PostAIAgent();
    const postAIAgentWithId = PostAIAgentWithId(botData?.aiagent);
    const getAIAgent = GetAIAgent(botData?.aiagent, isGemini && !!botData?.aiagent);

    // OpenAI vector store hooks
    const createVectorStore = CreateVectorStore();
    const addFileToVectorStore = AddFileToVectorStore();
    const removeFileFromVectorStore = RemoveFileFromVectorStore();
    const { data: vectorStoreFiles, refetch: refetchVectorStoreFiles } = GetVectorStoreFiles(botData?.vector_store_id, false);

    // Build assistant post body
    const buildAssistantPostBody = useMemo(() => {
        if (!assistantInstructionsConfig?.postBody || !botData?.config) return null;

        const processed = replaceInstructionVariables(
            assistantInstructionsConfig,
            supportBotInstructions,
            supportTopicInstructionBody,
            supportBotInstructionBody,
            botLanguage
        );

        // For Gemini, override templates with Gemini-specific ones
        if (isGemini) {
            mergeGeminiTemplates(processed, geminiConfig);
        }

        // Name: bot name + (id)
        processed.postBody.name = `${botData.config.name || "SupportBot"} (${botData.id || 'new'})`;

        // Resolve taxonomy titles in bot language
        const botTranslationMap = botLangTranslations?.data
            ? formatTranslationsToIntl(botLangTranslations.data)
            : null;
        const mergedTopicData = {};
        (botData.config?.supportTopics || []).forEach(code => {
            const data = botData.config?.topicData?.[code] || supportTopicCodes?.[code] || {};
            mergedTopicData[code] = {
                ...data,
                title: botTranslationMap
                    ? getTopicDisplayNameFromTranslations(code, data, botTranslationMap)
                    : (data.title || code)
            };
        });

        const botSection = processSingleBotInstructions({
            bot: botData,
            processedConfig: processed,
            intl,
            categoryData: mergedTopicData,
            translationMap: botTranslationMap
        });

        // Merge into main instructions (use Gemini-specific master prompt if available)
        const masterPrompt = (isGemini && geminiConfig?.systemInstructionTemplate)
            ? geminiConfig.systemInstructionTemplate
            : (processed.postBody.instructions || "");
        processed.postBody.instructions = masterPrompt
            .replace(/{botInstructions}/g, botSection.trim())
            .replace(/{initialMessage}/g, "")
            .replace(/{defaultLanguage}/g, getLanguageDisplayName(botLanguage));

        if (isGemini) {
            // Gemini tools come from geminiConfig.tools — only add RAG tool here
            processed.postBody.tools = ensureRagRetrievalTool(
                [],
                botData.config?.corpora || {}
            );
            return processed.postBody;
        }

        // OpenAI: Ensure file_search tool is present if there are materials
        processed.postBody.tools = ensureFileSearchTool(
            processed.postBody.tools,
            botData.config.materials
        );

        return processed.postBody;
    }, [assistantInstructionsConfig, botData, intl, supportBotInstructions, supportTopicInstructionBody, supportBotInstructionBody, botLanguage, isGemini, botLangTranslations?.data]);

    // Initialize bot data from API
    const initializeBotData = useCallback((id) => {
        // Check for new bot template
        if (id === 'new') {
            const templateData = sessionStorage.getItem('newBotTemplate');
            if (templateData) {
                const parsedTemplate = JSON.parse(templateData);

                return {
                    id: null,
                    updated: "",
                    updatedby: "",
                    created: "",
                    createdby: "",
                    status: "DRAFT",
                    config: {
                        ...parsedTemplate.config,
                        topicData: parsedTemplate.config.topicData || {}
                    }
                };
            }
        }

        const getBot = supportBots.find(bot => Number(bot.id) === Number(id));

        if (getBot) {
            const normalizedConfig = {
                ...getBot.config,
                topicData: getBot.config.topicData || {}
            };

            // Set version to DEFAULT if missing or empty
            if (!normalizedConfig?.version || normalizedConfig?.version === "") {
                normalizedConfig.version = "DEFAULT";
            }

            // Generate versionGroup if missing or empty
            if (!normalizedConfig?.versionGroup || normalizedConfig?.versionGroup === "") {
                normalizedConfig.versionGroup = getUniqueKey();
            }

            // Initialize versions array if missing or empty
            if (!normalizedConfig?.versions || normalizedConfig?.versions.length === 0) {
                normalizedConfig.versions = ["DEFAULT"];
            }

            return {
                ...getBot,
                config: normalizedConfig
            };
        }

        return defaultBotData

    }, [supportBots]);

    // Reset component when navigating to a different bot
    useEffect(() => {
        if (initialDataLoaded && supportBots) {
            const sessionData = BotSessionData.load(botId)
            const originalSessionData = BotSessionData.loadOriginal(botId) || null

            const initialData = sessionData || initializeBotData(botId)
            setBotData(initialData)

            if (sessionStorage.getItem('newBotTemplate')) {
                setOriginalBotData(defaultBotData)
                setHasChanges(true)

                BotSessionData.save(botId, initialData, defaultBotData);
                sessionStorage.removeItem('newBotTemplate');

                return;
            }

            const initialOriginalData = originalSessionData || JSON.parse(JSON.stringify(initialData))
            setOriginalBotData(initialOriginalData)

            if (sessionData && originalSessionData) {
                const hasChanges = JSON.stringify(sessionData) !== JSON.stringify(originalSessionData);
                setHasChanges(hasChanges)
            } else {
                setHasChanges(false)
            }
        }
    }, [botId, initializeBotData, initialDataLoaded, supportBots])

    const refreshDataOnDemand = useCallback(() => {
        if (refreshData) {
            refreshData()
        }
    }, [refreshData])

    // Track changes and persist to session storage
    useEffect(() => {
        if (botData && originalBotData) {
            const currentDataString = JSON.stringify(botData);
            const originalDataString = JSON.stringify(originalBotData);
            const dataHasChanges = currentDataString !== originalDataString;

            setHasChanges(dataHasChanges)

            if (dataHasChanges) {
                BotSessionData.save(botId, botData, originalBotData)
            } else {
                BotSessionData.clear(botId)
            }
        }
    }, [botData, originalBotData, botId])

    const updateRootData = useCallback((field, value) => {
        setBotData(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    const updateBotData = useCallback((field, value) => {
        setBotData(prev => ({
            ...prev,
            config: {
                ...prev.config,
                [field]: value
            }
        }));
    }, []);

    const updateNestedData = useCallback((parent, field, value) => {
        setBotData(prev => ({
            ...prev,
            config: {
                ...prev.config,
                [parent]: {
                    ...prev.config[parent],
                    [field]: value
                }
            }
        }));
    }, []);

    // Function to manage vector store (OpenAI only — Gemini uses shared Knowledge Bases)
    const handleVectorStore = async (updatedBotData) => {
        if (isGemini) {
            // Gemini bots use Knowledge Bases managed via Bot Competences — no per-bot corpus sync
            return null;
        }

        return await syncVectorStoreFiles({
            botData,
            updatedBotData,
            createVectorStoreFn: createVectorStore.mutateAsync,
            addFileFn: addFileToVectorStore.mutateAsync,
            removeFileFn: removeFileFromVectorStore.mutateAsync,
            refetchFilesFn: refetchVectorStoreFiles
        });
    };

    // Function to ensure assistant is created/updated (OpenAI only — Gemini aiagents are created at survey level)
    const ensureAssistantReady = async (updatedBotData, vectorStoreId) => {
        if (isGemini) return null;

        if (!buildAssistantPostBody) {
            console.warn("Assistant config missing, skipping assistant save");
            return updatedBotData.assistantid || null;
        }

        try {
            // --- OpenAI: existing flow ---
            const currentAssistantId = updatedBotData.assistantid;
            const postBody = _.cloneDeep(buildAssistantPostBody);

            // Add vector store to tool_resources if available
            if (vectorStoreId) {
                postBody.tool_resources = {
                    file_search: {
                        vector_store_ids: [vectorStoreId]
                    }
                };
            } else if (getAssistant.data?.tool_resources) {
                postBody.tool_resources = getAssistant.data.tool_resources;
            }

            // Update existing assistant
            if (currentAssistantId) {
                if (getAssistant.isLoading) {
                    let attempts = 0;
                    while (attempts < 30 && getAssistant.isLoading) {
                        await new Promise((r) => setTimeout(r, 200));
                        attempts += 1;
                    }
                }

                if (getAssistant.isSuccess && getAssistant.data) {
                    if (!postBody.tools && getAssistant.data.tools) {
                        postBody.tools = getAssistant.data.tools;
                    }

                    const res = await postAssistantWithId.mutateAsync(postBody);
                    return res?.data?.id || currentAssistantId;
                }
            }

            // Create new assistant
            const createRes = await postAssistant.mutateAsync(postBody);
            const newId = createRes?.data?.id;
            if (!newId) throw new Error("Assistant creation failed");
            return newId;

        } catch (error) {
            console.error("Error saving assistant/agent:", error);
            throw error;
        }
    };

    const handleSave = useCallback(async (newStatus = null, navigationLocation) => {
        // Set saving state to track which button was clicked
        setIsSaving(newStatus || 'ACTIVE');

        // Copy the current state
        const updatedBotData = { ...botData };

        // Check if we need to assign a versionGroup
        const needsVersionGroup =
            !updatedBotData?.config?.versionGroup ||
            updatedBotData.config.versionGroup === "";

        if (needsVersionGroup) {
            const uniqueKey = getUniqueKey()
            updatedBotData.config.versionGroup = uniqueKey
        }

        if (newStatus) {
            updatedBotData.status = newStatus
        }

        // Handle vector store (OpenAI only — Gemini uses shared Knowledge Bases)
        let vectorStoreId = null;
        try {
            vectorStoreId = await handleVectorStore(updatedBotData);
            if (vectorStoreId) {
                updatedBotData.vector_store_id = vectorStoreId;
            }
        } catch (error) {
            setIsSaving(null);
            setSnackbarData({
                show: true,
                text: intl.formatMessage({
                    id: 'vectorStoreSaveFailed',
                    defaultMessage: 'Failed to save reference materials. Please try again.'
                }),
                state: "error",
                autoHide: true,
            });
            return; // Stop save process if vector store fails
        }

        // Save OpenAI assistant (Gemini aiagents are created at survey level, not per module)
        if (!isGemini) {
            try {
                const assistantId = await ensureAssistantReady(updatedBotData, vectorStoreId);
                if (assistantId) {
                    updatedBotData.assistantid = assistantId;
                }
            } catch (error) {
                setIsSaving(null);
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage({
                        id: 'assistantSaveFailed',
                        defaultMessage: 'Failed to save assistant. Please try again.'
                    }),
                    state: "error",
                    autoHide: true,
                });
                return;
            }
        }

        // Create a copy of bot data without computed fields for the bot API
        const botDataForApi = {
            ...updatedBotData,
            config: { ...updatedBotData.config }
        };
        delete botDataForApi.config.topicData;
        delete botDataForApi.config.versions;

        // Save bot data (topicSettings is persisted as part of config)
        postBot.mutate(botDataForApi, {
            onError: error => {
                setIsSaving(null);
                console.error("Error saving bot:", error);
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage({
                        id: 'saveFailed',
                        defaultMessage: 'Failed to save changes. Please try again.'
                    }),
                    state: "error",
                    autoHide: true,
                });
            },
            onSuccess: (response) => {
                const savedBotId = response?.data?.id;
                setIsSaving(null);
                BotSessionData.clear(botId);
                refreshDataOnDemand();
                // Invalidate the bots list so any open survey editor picks up this change
                queryClient.invalidateQueries('bots');

                setOriginalBotData(JSON.parse(JSON.stringify(updatedBotData)));
                setHasChanges(false);

                setSnackbarData({
                    show: true,
                    text: intl.formatMessage({
                        id: 'saveSuccess',
                        defaultMessage: 'Changes saved successfully!'
                    }),
                    state: "success",
                    autoHide: true,
                });

                if (navigationLocation) {
                    history.push(navigationLocation);
                }

                if (savedBotId) {
                    const currentPath = history.location.pathname;
                    let currentTab = '/information';

                    if (currentPath.includes('/instructions')) {
                        currentTab = '/instructions';
                    } else if (currentPath.includes('/knowledge')) {
                        currentTab = '/knowledge';
                    } else if (currentPath.includes('/information')) {
                        currentTab = '/information';
                    }
                    history.push(`/support-bot/${savedBotId}${currentTab}`);
                }
            }
        });

        // Keep data in state for continued editing
        setBotData(updatedBotData);
    }, [botData, intl, botId, postBot, setSnackbarData, buildAssistantPostBody, getAssistant, postAssistant, postAssistantWithId]);

    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            const version = botData.config?.version;
            const versionGroup = botData.config?.versionGroup;
            const isDefault = version === "DEFAULT";

            const botsToDelete = isDefault && versionGroup
                ? supportBots.filter(b => b.config?.versionGroup === versionGroup)
                : [botData];

            for (const bot of botsToDelete) {
                await updateBotById(bot.id, { ...bot, status: "DELETED" });
                BotSessionData.clear(bot.id);
            }

            queryClient.invalidateQueries('bots');
            setSnackbarData({
                show: true,
                text: intl.formatMessage({ id: 'bot.deleteSuccess', defaultMessage: 'Bot module deleted.' }),
                state: 'success',
                autoHide: true,
            });
            setShowDeleteModal(false);
            history.push('/support-bots');
        } catch (err) {
            console.error('Error deleting bot module:', err);
            setSnackbarData({
                show: true,
                text: intl.formatMessage({ id: 'bot.deleteError', defaultMessage: 'Failed to delete bot module. Please try again.' }),
                state: 'error',
                autoHide: true,
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const getDeleteModalText = () => {
        const version = botData.config?.version;
        const versionGroup = botData.config?.versionGroup;
        const botName = botData.config?.name || `Bot #${botId}`;
        const isDefault = version === "DEFAULT";

        const allVersionBots = isDefault && versionGroup
            ? supportBots.filter(b => b.config?.versionGroup === versionGroup)
            : [botData];
        const isActive = allVersionBots.some(b => b.status === "ACTIVE");
        const allVersionNames = allVersionBots.map(b => b.config?.version).filter(Boolean);

        let text = '';
        if (isDefault && allVersionNames.length > 1) {
            const otherVersions = allVersionNames.filter(v => v !== "DEFAULT").join(', ');
            text = intl.formatMessage(
                { id: 'bot.deleteAllVersions', defaultMessage: 'Deleting the default version will delete {botName} and all its versions ({versions}).' },
                { botName, versions: otherVersions }
            );
        } else if (isDefault) {
            text = intl.formatMessage(
                { id: 'bot.deleteModule', defaultMessage: 'Are you sure you want to delete {botName}?' },
                { botName }
            );
        } else {
            text = intl.formatMessage(
                { id: 'bot.deleteVersion', defaultMessage: 'Are you sure you want to delete version {version} of {botName}?' },
                { version, botName }
            );
        }

        if (isActive) {
            text += ' ' + intl.formatMessage({ id: 'bot.deleteActiveWarning', defaultMessage: 'This bot is currently active and may be in use.' });
        }

        return text;
    };

    const [navigateTo, setNavigateTo] = useState(null)

    useEffect(() => {
        if (navigateTo && !hasChanges) {
            hidePopup();
            history.push(navigateTo);
            setNavigateTo(null);
        }
    }, [hasChanges, navigateTo, hidePopup, history])

    const handleDiscard = useCallback((location) => {
        BotSessionData.clear(botId)
        setHasChanges(false)
        setNavigateTo(location)
    }, [])

    const showUnsavedChangesPrompt = useCallback((location) => {
        showPopup(
            <UnsavedChangesPrompt
                handleSave={() => {
                    hidePopup()
                    handleSave(null, location)
                }}
                onCancel={() => {
                    hidePopup()
                }}
                onDiscard={() => {
                    handleDiscard(location)
                }}
            />,
            'confirm'
        )
    }, [showPopup, hidePopup, handleSave, handleDiscard])

    if (!botData) {
        return <BotEditorSkeleton />
    }

    const isDraft = botData?.status?.toLowerCase() === "draft";

    const placeholderName = intl.formatMessage({id: "createNewSupportBot", defaultMessage: "New Bot Module"})

    return (
        <div className={"survey-container bot-editor"}>
            <Prompt
                when={hasChanges}
                message={(location) => {
                    if (location.pathname.includes(`/support-bot/${botId}/`)) {
                        return true;
                    }

                    showUnsavedChangesPrompt(location);
                    return false;
                }}
            />
            <div className={"survey-header header"}>
                <Link to="/support-bots" className={"back"}>
                    <BackArrow />
                </Link>
                <h3>{botData.config.name || placeholderName}</h3>
                <BotStatus
                    status={botData?.status || "DRAFT"}
                    updateBotData={updateRootData}
                    onDelete={!newBot ? () => setShowDeleteModal(true) : null}
                />
                {hasChanges &&
                    <p className={"unsaved-changes"}>
                        {intl.formatMessage({
                            id: 'survey.header.unsavedChanges',
                            defaultMessage: 'Unsaved changes',
                        })}
                    </p>
                }
                <SaveEdits
                    handleSave={handleSave}
                    hasChanges={hasChanges}
                    botData={supportBots}
                    botId={botId}
                    currentBot={botData}
                    isDraft={isDraft}
                    isSaving={isSaving}
                />
            </div>
            <div className={"survey-view"}>
                <div className={"survey-content"}>
                    <BotNavigation url={url} />
                    <BotContent
                        url={url}
                        path={path}
                        botData={botData.config}
                        updateBotData={updateBotData}
                        updateNestedData={updateNestedData}
                    />
                </div>
                <BotModuleTester botData={botData} updateRootData={updateRootData}/>
            </div>

            {showDeleteModal && (
                <Modal
                    header={intl.formatMessage({ id: 'bot.deleteTitle', defaultMessage: 'Delete bot module' })}
                    text={getDeleteModalText()}
                    closeModal={() => setShowDeleteModal(false)}
                    confirmAction={handleDelete}
                    confirmDisabled={isDeleting}
                    confirmText={isDeleting
                        ? intl.formatMessage({ id: 'deleting', defaultMessage: 'Deleting...' })
                        : intl.formatMessage({ id: 'survey.delete', defaultMessage: 'Delete' })
                    }
                    alert={true}
                />
            )}
        </div>
    );
};

const BotStatus = ({status, updateBotData, onDelete}) => {
    const intl = useIntl()
    const handleStatusChange = (e) => {
        const value = e.target.value;
        if (value === "DELETED") {
            onDelete?.();
            // Reset select back to current status
            e.target.value = status;
        } else {
            updateBotData("status", value);
        }
    };

    return <div className={`bot-status ${status}`}>
        <StatusIcon />
        {intl.formatMessage({
            defaultMessage: status,
            id: `bot.status.${status}`,
        })}
        <div className={"change-status"}>
            <button className={"status-button"}>
                {intl.formatMessage({
                    defaultMessage:"Edit",
                    id:"edit"
                })}
            </button>
            <select
                className={"status-select"}
                value={status}
                onChange={handleStatusChange}>
                <option value={"DRAFT"}>
                    {intl.formatMessage({
                        defaultMessage: "Draft",
                        id: `bot.status.DRAFT`,
                    })}
                </option>
                <option value={"ACTIVE"}>
                    {intl.formatMessage({
                        defaultMessage: "Active",
                        id: `bot.status.ACTIVE`,
                    })}
                </option>
                {onDelete && (
                    <option value={"DELETED"}>
                        {intl.formatMessage({
                            defaultMessage: "Delete",
                            id: `survey.delete`,
                        })}
                    </option>
                )}
            </select>
        </div>
    </div>
}

const BotNavigation = ({ url }) => {
    const intl = useIntl();
    const { botId } = useParams();
    const { aiProvider } = useSupportBotData();

    return (
        <div className={"survey-navigation"}>
            <nav>
                <ul>
                    <li>
                        <NavLink to={`${url}/information`}>
                            <Settings size={16} />
                            {intl.formatMessage({
                                id: 'survey.navigation.information',
                                defaultMessage: 'Information',
                            })}
                        </NavLink>
                    </li>
                    <li>
                        <NavLink to={`${url}/instructions`}>
                            <BookOpen size={16} />
                            {intl.formatMessage({
                                id: 'instructions',
                                defaultMessage: 'Instructions',
                            })}
                        </NavLink>
                    </li>
                    <li>
                        <NavLink to={`${url}/knowledge`}>
                            <Files size={16} />
                            {intl.formatMessage({
                                id: 'knowledge',
                                defaultMessage: 'Knowledge',
                            })}
                        </NavLink>
                    </li>
                </ul>
            </nav>
        </div>
    );
};

const BotContent = ({ url, path, botData, updateBotData, updateNestedData }) => {
    return (
        <Switch>
            <Route path={`${path}/information`}>
                <div className={`survey-edit bot-content`}>
                    <BotInformation
                        botData={botData}
                        updateBotData={updateBotData}
                    />
                </div>
            </Route>
            <Route path={`${path}/instructions`}>
                <div className={`survey-edit bot-content`}>
                    <BotInstructions
                        botData={botData}
                        updateBotData={updateBotData}
                        updateNestedData={updateNestedData}
                    />
                </div>
            </Route>
            <Route path={`${path}/knowledge`}>
                <div className={`survey-edit bot-content`}>
                    <BotKnowledge
                        botData={botData}
                        updateBotData={updateBotData}
                    />
                </div>
            </Route>
            <Route path={path}>
                <Redirect to={`${url}/information`} />
            </Route>
        </Switch>
    );
};

const SaveEdits = ({ handleSave, hasChanges, botData, botId, currentBot, isDraft, isSaving }) => {
    const intl = useIntl();
    const isAnySaving = isSaving !== null;

    return (
        <div className={"save-toolbar"}>
            <BotVersionSelector botData={botData} botId={botId} currentBot={currentBot}/>
            {isDraft ?
            <>
                <button
                    className={`draft ${!hasChanges || isAnySaving ? 'disabled' : ''}`}
                    onClick={()=>handleSave('DRAFT')}
                    disabled={!hasChanges || isAnySaving}
                >
                    {isSaving === 'DRAFT' ? (
                        <>
                            <Loader2 size={16} className="spinner" />
                            {intl.formatMessage({
                                defaultMessage: 'Saving...',
                                id: 'saving'
                            })}
                        </>
                    ) : (
                        intl.formatMessage({
                            defaultMessage: 'Save draft',
                            id: 'saveDraft'
                        })
                    )}
                </button>
                <button
                    className={`save ${isAnySaving ? 'disabled' : ''}`}
                    onClick={()=>handleSave('ACTIVE')}
                    disabled={isAnySaving}
                >
                    {isSaving === 'ACTIVE' ? (
                        <>
                            <Loader2 size={16} className="spinner" />
                            {intl.formatMessage({
                                defaultMessage: 'Publishing...',
                                id: 'publishing'
                            })}
                        </>
                    ) : (
                        intl.formatMessage({
                            defaultMessage: 'Publish',
                            id: 'publish'
                        })
                    )}
                </button>
            </> :
                <button
                    className={`save ${!hasChanges || isAnySaving ? 'disabled' : ''}`}
                    onClick={()=>handleSave('ACTIVE')}
                    disabled={!hasChanges || isAnySaving}
                >
                    {isSaving === 'ACTIVE' ? (
                        <>
                            <Loader2 size={16} className="spinner" />
                            {intl.formatMessage({
                                defaultMessage: 'Saving...',
                                id: 'saving'
                            })}
                        </>
                    ) : (
                        intl.formatMessage({
                            defaultMessage: 'Save changes',
                            id: 'saveChanges'
                        })
                    )}
                </button>
            }
        </div>
    );
};

function BotEditorSkeleton() {
    return <div className={"survey-container skeleton-container"}>
        <div className={"survey-header"}>
            <Skeleton width={17} height={17} circle={true} noMargin={true} classText={"back"}/>
            <Skeleton width={250} height={24} noMargin={true}/>
            <Skeleton width={57} height={17} noMargin={true} classText={"status"}/>
            <div className={"save-toolbar"}>
                <Skeleton width={150} height={40} noMargin={true} classText={"draft"}/>
                <Skeleton width={90} height={40} noMargin={true} classText={"save"}/>
            </div>
        </div>
        <div className={"survey-view"}>
            <div className={"survey-content"}>
                <div className={"survey-navigation"}>
                    <Skeleton width={80} height={20} noMargin={true}/>
                    <Skeleton width={80} height={20} noMargin={true}/>
                    <Skeleton width={80} height={20} noMargin={true}/>
                </div>
                <div className={"survey-edit"}>
                    <Skeleton width={40} height={20}/>
                    <Skeleton height={55} noMargin={true} classText={"input"}/>
                    <div className={"survey-time"}>
                        <div>
                            <Skeleton width={40} height={20}/>
                            <Skeleton height={55} noMargin={true} classText={"input"}/>
                        </div>
                        <div>
                            <Skeleton width={40} height={20}/>
                            <Skeleton height={55} noMargin={true} classText={"input"}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
}

const UnsavedChangesPrompt = ({ handleSave, onCancel, onDiscard }) => {
    const intl = useIntl();

    return (
        <Modal
            header={intl.formatMessage({
                id: 'survey.unsavedChanges.header',
                defaultMessage: 'Save changes?',
            })}
            text={
                <FormattedMessage
                    id="survey.unsavedChanges.text"
                    defaultMessage="Do you wish to save changes before leaving page?{br}Any unsaved changes will be lost."
                    values={{ br: <br /> }}
                />
            }
            discardText={intl.formatMessage({
                id: 'survey.unsavedChanges.discard',
                defaultMessage: "Don't save",
            })}
            confirmText={intl.formatMessage({
                id: 'survey.save.save',
                defaultMessage: "Save changes",
            })}
            discardAction={onDiscard}
            closeModal={onCancel}
            confirmAction={handleSave}
        />
    );
};